import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { formatCurrency } from '../utils/formatUtils';
import { useNavigate } from 'react-router-dom';
import { 
  Users, 
  DollarSign,
  TrendingUp,
  Calendar,
  UserCheck,
  UserPlus,
  Target,
  Activity,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Check,
  FileText,
  BarChart3,
  ListFilter,
  X,
  Database,
  Settings,
  HelpCircle
} from 'lucide-react';
import axios from 'axios';
import { useBrand } from '../contexts/BrandContext';
import { useLanguage } from '../contexts/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const { user } = useContext(AuthContext);
  const { brandName, primaryColor, logoUrl, refreshBrand } = useBrand();
  const { t, tt } = useLanguage();
  const navigate = useNavigate();
  const [showBackupPassword, setShowBackupPassword] = useState(false);
  const [backupLoading, setBackupLoading] = useState(false);
  const [stats, setStats] = useState({
    totalClients: 0,
    ftdClients: 0,
    newClients: 0,
    totalBalance: 0,
    totalDeposits: 0,
    totalDepositAmount: 0
  });
  const [managerStats, setManagerStats] = useState({
    sales: [],
    retention: []
  });
  const [monthlyStats, setMonthlyStats] = useState([]);
  const [affiliatesCollapsed, setAffiliatesCollapsed] = useState(true);
  const [deletedManagersCollapsed, setDeletedManagersCollapsed] = useState(true);
  const [monthlyStatsCollapsed, setMonthlyStatsCollapsed] = useState(false);
  const [affiliates, setAffiliates] = useState([]);
  const [affiliateStats, setAffiliateStats] = useState([]);
  const [importStats, setImportStats] = useState([]);
  const [importStatsCollapsed, setImportStatsCollapsed] = useState(true);
  const [allCampaigns, setAllCampaigns] = useState([]);
  const [editingAffiliate, setEditingAffiliate] = useState(null);
  const [selectedCampaigns, setSelectedCampaigns] = useState([]);
  const [campaignSearch, setCampaignSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [departmentStatsCollapsed, setDepartmentStatsCollapsed] = useState(false);

  // Brand & Master Password settings (superadmin)
  const [showBrandSettings, setShowBrandSettings] = useState(false);
  const [brandForm, setBrandForm] = useState({
    brand_name: '',
    primary_color: '#16a34a',
    secondary_color: '#059669',
    accent_color: '#10b981',
    theme_preset: 'emerald',
    has_logo: false,
    font_family: 'Inter',
  });
  const [masterPwForm, setMasterPwForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [tradingMasterPw, setTradingMasterPw] = useState({ current_password: '', new_password: '' });
  const [pwStatus, setPwStatus] = useState({ crm: false, trading: false });
  const [brandSaving, setBrandSaving] = useState(false);
  // Partners management
  const [partners, setPartners] = useState([]);
  const [newPartnerName, setNewPartnerName] = useState('');
  const [newPartnerFile, setNewPartnerFile] = useState(null);
  const [partnerSaving, setPartnerSaving] = useState(false);
  // Factory Reset (white-label wipe)
  const [factoryResetPw, setFactoryResetPw] = useState('');
  const [factoryResetConfirm, setFactoryResetConfirm] = useState('');
  const [factoryResetting, setFactoryResetting] = useState(false);
  const [factoryResetResult, setFactoryResetResult] = useState(null);

  // Predefined harmonious theme palettes
  const THEME_PRESETS = [
    { key: 'emerald',   name: 'Emerald Pro',   primary: '#16a34a', secondary: '#059669', accent: '#10b981' },
    { key: 'ocean',     name: 'Ocean Blue',    primary: '#2563eb', secondary: '#1e40af', accent: '#3b82f6' },
    { key: 'royal',     name: 'Royal Purple',  primary: '#7c3aed', secondary: '#6d28d9', accent: '#a78bfa' },
    { key: 'slate',     name: 'Corporate Slate', primary: '#475569', secondary: '#334155', accent: '#64748b' },
    { key: 'sunset',    name: 'Sunset Orange', primary: '#ea580c', secondary: '#c2410c', accent: '#fb923c' },
    { key: 'midnight',  name: 'Midnight Dark', primary: '#0f172a', secondary: '#1e293b', accent: '#38bdf8' },
    { key: 'crimson',   name: 'Crimson Red',   primary: '#dc2626', secondary: '#991b1b', accent: '#f87171' },
    { key: 'gold',      name: 'Luxury Gold',   primary: '#b45309', secondary: '#78350f', accent: '#f59e0b' },
  ];

  // Month filter
  const now = new Date();
  const [filterMonth, setFilterMonth] = useState(now.getMonth() + 1);
  const [filterYear, setFilterYear] = useState(now.getFullYear());

  // Per-campaign status expand
  const [expandedCampaignStatuses, setExpandedCampaignStatuses] = useState({});

  useEffect(() => {
    fetchDashboardData();
    if (user?.role === 'superadmin') {
      fetchAffiliates();
    }
  }, [filterMonth, filterYear]);

  useEffect(() => {
    if (user?.role === 'superadmin') {
      fetchAffiliates();
      fetchImportStats();
    }
  }, [user]);

  const loadBrandSettings = async () => {
    try {
      const res = await axios.get(`${API}/brand/settings`);
      setBrandForm({
        brand_name: res.data.brand_name || '',
        primary_color: res.data.primary_color || '#16a34a',
        secondary_color: res.data.secondary_color || '#059669',
        accent_color: res.data.accent_color || '#10b981',
        theme_preset: res.data.theme_preset || 'emerald',
        has_logo: res.data.has_logo || false,
        font_family: res.data.font_family || 'Inter',
      });
      const pwRes = await axios.get(`${API}/brand/master-password-status`);
      setPwStatus({ crm: pwRes.data.has_crm_master_password, trading: pwRes.data.has_trading_master_password });
      loadPartners();
    } catch (e) { /* */ }
  };

  const loadPartners = async () => {
    try {
      const res = await axios.get(`${API}/brand/partners`);
      setPartners(Array.isArray(res.data) ? res.data : []);
    } catch { setPartners([]); }
  };

  const applyThemePreset = (preset) => {
    setBrandForm(prev => ({
      ...prev,
      theme_preset: preset.key,
      primary_color: preset.primary,
      secondary_color: preset.secondary,
      accent_color: preset.accent,
    }));
  };

  const saveBrandSettings = async () => {
    setBrandSaving(true);
    try {
      await axios.put(`${API}/brand/settings`, {
        brand_name: brandForm.brand_name,
        primary_color: brandForm.primary_color,
        secondary_color: brandForm.secondary_color,
        accent_color: brandForm.accent_color,
        theme_preset: brandForm.theme_preset,
        font_family: brandForm.font_family,
      });
      refreshBrand();
      alert(tt('Настройки бренда сохранены'));
    } catch (e) { alert(e.response?.data?.detail || 'Ошибка'); }
    finally { setBrandSaving(false); }
  };

  const handleAddPartner = async () => {
    if (!newPartnerName.trim()) return alert(tt('Введите название партнёра'));
    if (!newPartnerFile) return alert(tt('Загрузите логотип партнёра'));
    setPartnerSaving(true);
    try {
      const fd = new FormData();
      fd.append('name', newPartnerName.trim());
      fd.append('logo', newPartnerFile);
      fd.append('order', String(partners.length));
      await axios.post(`${API}/brand/partners`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setNewPartnerName('');
      setNewPartnerFile(null);
      await loadPartners();
    } catch (e) { alert(e.response?.data?.detail || 'Ошибка добавления партнёра'); }
    finally { setPartnerSaving(false); }
  };

  const handleDeletePartner = async (id) => {
    if (!window.confirm(tt('Удалить партнёра?'))) return;
    try {
      await axios.delete(`${API}/brand/partners/${id}`);
      await loadPartners();
    } catch (e) { alert(e.response?.data?.detail || 'Ошибка'); }
  };

  const handleTogglePartner = async (partner) => {
    try {
      await axios.put(`${API}/brand/partners/${partner.id}`, { enabled: !partner.enabled });
      await loadPartners();
    } catch (e) { alert(e.response?.data?.detail || 'Ошибка'); }
  };

  const handleFactoryReset = async () => {
    if (factoryResetConfirm !== 'УДАЛИТЬ ВСЁ') {
      alert(tt('Введите фразу подтверждения точно: УДАЛИТЬ ВСЁ'));
      return;
    }
    if (!factoryResetPw) {
      alert(tt('Введите мастер-пароль'));
      return;
    }
    if (!window.confirm(tt('⚠️ ПРЕДУПРЕЖДЕНИЕ 1 из 3\n\nВы собираетесь ПОЛНОСТЬЮ УДАЛИТЬ базу данных.\n\nБудут стёрты:\n• Все клиенты и их данные\n• Все депозиты и выводы\n• Все сделки и торговая история\n• Все сотрудники (кроме вас)\n• Все настройки бренда и интеграции\n\nПродолжить?'))) return;
    if (!window.confirm(tt('⚠️ ПРЕДУПРЕЖДЕНИЕ 2 из 3\n\nЭто действие НЕОБРАТИМО.\n\nДанные НЕ будут отправлены в корзину — они удаляются из базы навсегда. Восстановление возможно ТОЛЬКО из ранее скачанного бэкапа.\n\nВы точно скачали бэкап, если он вам нужен?\n\nПродолжить удаление?'))) return;
    const finalPhrase = window.prompt(tt('⚠️ ПРЕДУПРЕЖДЕНИЕ 3 из 3 (ПОСЛЕДНЕЕ)\n\nДля окончательного подтверждения введите фразу:\n\nЯ ПОДТВЕРЖДАЮ УДАЛЕНИЕ\n\n(точно, с большой буквы и с пробелами)'));
    if (finalPhrase !== 'Я ПОДТВЕРЖДАЮ УДАЛЕНИЕ') {
      alert(tt('Фраза не совпадает. Заводской сброс отменён.'));
      return;
    }
    setFactoryResetting(true);
    setFactoryResetResult(null);
    try {
      const response = await axios.post(`${API}/admin/factory-reset`, {
        password: factoryResetPw,
        confirm: factoryResetConfirm,
      });
      setFactoryResetResult({ status: 'success', ...response.data });
      setFactoryResetPw('');
      setFactoryResetConfirm('');
      setTimeout(() => window.location.reload(), 3000);
    } catch (error) {
      setFactoryResetResult({
        status: 'error',
        message: error.response?.data?.detail || error.message || tt('Ошибка сброса'),
      });
    } finally {
      setFactoryResetting(false);
    }
  };

  const saveMasterPassword = async () => {
    if (masterPwForm.new_password !== masterPwForm.confirm_password) return alert(tt('Пароли не совпадают'));
    if (!masterPwForm.current_password) return alert(tt('Введите пароль суперадмина'));
    try {
      await axios.put(`${API}/brand/master-password`, { current_password: masterPwForm.current_password, master_password: masterPwForm.new_password });
      setMasterPwForm({ current_password: '', new_password: '', confirm_password: '' });
      setPwStatus(prev => ({...prev, crm: true}));
      alert(tt('Мастер-пароль CRM обновлён'));
    } catch (e) {
      const msg = e.response?.data?.detail || 'Ошибка';
      if (msg.includes('authentication')) alert(tt('Сессия истекла. Перезайдите в систему.'));
      else alert(msg);
    }
  };

  const saveTradingMasterPw = async () => {
    if (!tradingMasterPw.current_password) return alert(tt('Введите пароль суперадмина'));
    try {
      await axios.put(`${API}/brand/trading-master-password`, { current_password: tradingMasterPw.current_password, trading_master_password: tradingMasterPw.new_password });
      setTradingMasterPw({ current_password: '', new_password: '' });
      setPwStatus(prev => ({...prev, trading: !!tradingMasterPw.new_password}));
      alert(tradingMasterPw.new_password ? 'Мастер-пароль торговой платформы обновлён' : 'Мастер-пароль торговой платформы удалён');
    } catch (e) {
      const msg = e.response?.data?.detail || 'Ошибка';
      if (msg.includes('authentication')) alert(tt('Сессия истекла. Перезайдите в систему.'));
      else alert(msg);
    }
  };


  const fetchAffiliates = async () => {
    try {
      const token = localStorage.getItem('token');
      // Загружаем affiliate-tokens для списка
      const tokensResponse = await axios.get(`${API}/affiliate-tokens`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const tokens = tokensResponse.data;
      setAffiliates(tokens);
      
      // Загружаем реальную статистику аффилиатов с фильтром по месяцу
      try {
        const params = new URLSearchParams();
        if (filterMonth) {
          params.append('month', filterMonth);
          params.append('year', filterYear);
        }
        const statsResponse = await axios.get(`${API}/affiliate-tokens/stats?${params}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setAffiliateStats(statsResponse.data);
      } catch (statsError) {
        console.error('Error fetching affiliate stats:', statsError);
        // Fallback: показываем с нулями если не удалось загрузить
        const fallbackStats = tokens.map(t => ({
          name: t.name,
          status: t.is_active ? 'active' : 'inactive',
          totalClients: 0,
          ftdClients: 0,
          conversionRate: 0,
          totalBalance: 0,
          campaigns: []
        }));
        setAffiliateStats(fallbackStats);
      }
    } catch (error) {
      console.error('Error fetching affiliates:', error);
    }
  };

  const fetchImportStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/import-stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setImportStats(response.data);
    } catch (error) {
      console.error('Error fetching import stats:', error);
    }
  };

  const fetchAllCampaigns = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/campaigns/list`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setAllCampaigns(response.data);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    }
  };

  const handleEditCampaigns = (affiliate) => {
    const token = affiliates.find(a => a.name === affiliate.name);
    setEditingAffiliate(affiliate);
    setSelectedCampaigns(token?.linked_campaigns || []);
    setCampaignSearch('');
    if (allCampaigns.length === 0) fetchAllCampaigns();
  };

  const handleSaveCampaigns = async () => {
    if (!editingAffiliate) return;
    try {
      const token = localStorage.getItem('token');
      const affToken = affiliates.find(a => a.name === editingAffiliate.name);
      if (!affToken) return;
      
      await axios.put(`${API}/affiliate-tokens/${affToken.id}/campaigns`, {
        linked_campaigns: selectedCampaigns
      }, { headers: { 'Authorization': `Bearer ${token}` } });
      
      setEditingAffiliate(null);
      fetchAffiliates(); // Обновляем статистику
    } catch (error) {
      console.error('Error saving campaigns:', error);
    }
  };

  const toggleCampaign = (campaignName) => {
    setSelectedCampaigns(prev => 
      prev.includes(campaignName)
        ? prev.filter(c => c !== campaignName)
        : [...prev, campaignName]
    );
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Build filter params
      const params = new URLSearchParams();
      if (filterMonth) {
        params.append('month', filterMonth);
        params.append('year', filterYear);
      }
      
      // Fetch stats from backend
      const statsResponse = await axios.get(`${API}/clients/stats?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setStats({
        totalClients: statsResponse.data.total_clients || 0,
        ftdClients: statsResponse.data.ftd_clients || 0,
        ftdClean: statsResponse.data.ftd_clean || 0,
        ftdDirty: statsResponse.data.ftd_dirty || 0,
        newClients: statsResponse.data.new_clients || 0,
        inProgress: statsResponse.data.in_progress || 0,
        potential: statsResponse.data.potential || 0,
        notPotential: statsResponse.data.not_potential || 0,
        totalDeposits: statsResponse.data.total_deposits || 0,
        depositsCount: statsResponse.data.deposits_count || 0,
        conversionRate: statsResponse.data.conversion_rate || 0,
        conversionClean: statsResponse.data.conversion_clean || 0,
        conversionDirty: statsResponse.data.conversion_dirty || 0
      });
      
      // Fetch monthly stats (always all months, not filtered)
      const monthlyResponse = await axios.get(`${API}/clients/monthly-stats?months=6`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setMonthlyStats(monthlyResponse.data.monthly_stats || []);
      
      // Fetch manager stats for superadmin and team leads (with month filter)
      if (['superadmin', 'team_lead_sales', 'team_lead_retention'].includes(user?.role)) {
        const mgrParams = new URLSearchParams();
        if (filterMonth) {
          mgrParams.append('month', filterMonth);
          mgrParams.append('year', filterYear);
        }
        const managerStatsResponse = await axios.get(`${API}/managers/stats?${mgrParams}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        setManagerStats({ 
          sales: managerStatsResponse.data.sales || [], 
          retention: managerStatsResponse.data.retention || [],
          deleted: []
        });
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setLoading(false);
    }
  };

  return (
    <div className="page-container">
      {/* Welcome Section */}
      <div className="page-header">
        <div>
          <h1 className="page-title">{t('dash_title')}</h1>
          <p className="page-subtitle">{t('dash_welcome')} {user?.name || user?.email}</p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Trading Platform Link */}
          <a
            href="/trading" 
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-5 py-2.5 rounded-lg font-medium text-sm transition-all duration-200 shadow-md hover:shadow-lg"
          >
            <TrendingUp className="w-4 h-4" />
            <span>{t('dash_trading_terminal')}</span>
          </a>
          
          {/* Statistics Page Link */}
          {['superadmin'].includes(user?.role) && (
            <button
              onClick={() => navigate('/crm/stats')}
              className="flex items-center gap-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white px-5 py-2.5 rounded-lg font-medium text-sm transition-all duration-200 shadow-md hover:shadow-lg"
              data-testid="goto-stats-btn"
            >
              <BarChart3 className="w-4 h-4" />
              <span>{t('dash_stats_by_department')}</span>
            </button>
          )}

          {/* Admin Guide - superadmin only */}
          {user?.role === 'superadmin' && (
            <button
              onClick={() => {
                const token = localStorage.getItem('token');
                fetch(`${API}/admin-guide/pdf`, {
                  headers: { 'Authorization': `Bearer ${token}` }
                }).then(r => r.blob()).then(blob => {
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = 'admin_guide.pdf';
                  a.click();
                  URL.revokeObjectURL(url);
                });
              }}
              data-testid="download-admin-guide"
              className="flex items-center gap-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 px-3 py-2 rounded-lg text-xs transition-colors"
              title={tt('Скачать Admin Guide (PDF)')}
            >
              <FileText className="w-3.5 h-3.5" />
              <span>{t('dash_guide')}</span>
            </button>
          )}

          {/* DB Backup - superadmin only */}
          {user?.role === 'superadmin' && (
            <button
              onClick={() => setShowBackupPassword(true)}
              disabled={backupLoading}
              data-testid="download-db-backup"
              className="flex items-center gap-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 px-3 py-2 rounded-lg text-xs transition-colors"
              title={tt('Скачать полный бэкап базы данных')}
            >
              <Database className="w-3.5 h-3.5" />
              <span>{backupLoading ? t('loading') : t('dash_backup')}</span>
            </button>
          )}

          {/* Brand Settings - superadmin only */}
          {user?.role === 'superadmin' && (
            <button
              onClick={() => { setShowBrandSettings(!showBrandSettings); if (!showBrandSettings) loadBrandSettings(); }}
              data-testid="brand-settings-btn"
              className="flex items-center gap-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 px-3 py-2 rounded-lg text-xs transition-colors"
              title={tt('Настройки бренда и мастер-пароль')}
            >
              <Settings className="w-3.5 h-3.5" />
              <span>{t('dash_settings')}</span>
            </button>
          )}
        </div>
      </div>

      {/* Brand Settings Panel */}
      {showBrandSettings && user?.role === 'superadmin' && (
        <div className="bg-white rounded-xl shadow-sm border p-6 mb-6" data-testid="brand-settings-panel">
          <h3 className="text-base font-semibold mb-4">{t('brand_title')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="text-xs text-gray-500 font-medium block mb-1">{t('brand_company_name')}</label>
              <input className="w-full border rounded-lg px-3 py-2 text-sm" value={brandForm.brand_name} onChange={e => setBrandForm({...brandForm, brand_name: e.target.value})} placeholder={tt('Название')} data-testid="brand-name-input" />
            </div>
            <div>
              <label className="text-xs text-gray-500 font-medium block mb-1">{t('brand_font')}</label>
              <select className="w-full border rounded-lg px-3 py-2 text-sm" value={brandForm.font_family || 'Inter'} onChange={e => setBrandForm({...brandForm, font_family: e.target.value})} data-testid="brand-font-select">
                {['Inter', 'Roboto', 'Open Sans', 'Montserrat', 'Poppins', 'Raleway', 'Playfair Display', 'Oswald', 'Lato', 'Nunito', 'Source Sans Pro', 'Ubuntu', 'Merriweather', 'PT Sans', 'Rubik'].map(f => (
                  <option key={f} value={f} style={{fontFamily: f}}>{f}</option>
                ))}
              </select>
              <p className="text-[10px] text-gray-400 mt-0.5" style={{fontFamily: brandForm.font_family || 'Inter'}}>{t('brand_preview')} {brandForm.brand_name || brandName}</p>
            </div>
            <div>
              <label className="text-xs text-gray-500 font-medium block mb-1">{t('brand_primary_color')}</label>
              <div className="flex gap-2">
                <input type="color" className="w-10 h-9 border rounded cursor-pointer" value={brandForm.primary_color} onChange={e => setBrandForm({...brandForm, primary_color: e.target.value, theme_preset: 'custom'})} />
                <input className="flex-1 border rounded-lg px-3 py-2 text-sm font-mono" value={brandForm.primary_color} onChange={e => setBrandForm({...brandForm, primary_color: e.target.value, theme_preset: 'custom'})} data-testid="brand-color-input" />
              </div>
            </div>
            <div>
              <label className="text-xs text-gray-500 font-medium block mb-1">{t('brand_logo')}</label>
              <div className="flex items-center gap-2">
                {brandForm.has_logo && <img src={`${API}/brand/logo?t=${Date.now()}`} alt="Logo" className="h-9 w-auto rounded border" />}
                <label className="flex-1 border border-dashed rounded-lg px-3 py-2 text-sm text-center cursor-pointer hover:bg-gray-50">
                  <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    if (file.size > 10 * 1024 * 1024) return alert(tt('Макс. 10MB'));
                    const fd = new FormData();
                    fd.append('logo', file);
                    try {
                      await axios.post(`${API}/brand/upload-logo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                      setBrandForm({...brandForm, has_logo: true});
                      refreshBrand();
                    } catch(err) { alert(err.response?.data?.detail || 'Ошибка загрузки'); }
                  }} data-testid="brand-logo-upload" />
                  {brandForm.has_logo ? t('brand_replace') : t('brand_upload')}
                </label>
                {brandForm.has_logo && <button onClick={async () => {
                  try {
                    await axios.put(`${API}/brand/settings`, { logo_url: '' });
                    setBrandForm({...brandForm, has_logo: false});
                    refreshBrand();
                  } catch(e) { alert(tt('Ошибка')); }
                }} className="text-xs text-red-500 hover:text-red-700">{t('brand_delete')}</button>}
              </div>
              <p className="text-[10px] text-gray-400 mt-0.5">{t('brand_logo_hint')}</p>
            </div>
          </div>

          {/* Harmonious Theme Presets */}
          <div className="mb-4" data-testid="theme-presets">
            <label className="text-xs text-gray-500 font-medium block mb-2">{tt('Готовые гармоничные палитры')}</label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {THEME_PRESETS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => applyThemePreset(p)}
                  className={`flex items-center gap-2 border rounded-lg px-2.5 py-2 text-xs hover:shadow-md transition-all ${brandForm.theme_preset === p.key ? 'ring-2 ring-offset-1 ring-blue-500 border-blue-500 bg-blue-50' : 'border-gray-200 bg-white'}`}
                  data-testid={`theme-preset-${p.key}`}
                >
                  <div className="flex gap-0.5">
                    <span className="w-3 h-4 rounded-sm" style={{background: p.primary}}></span>
                    <span className="w-3 h-4 rounded-sm" style={{background: p.secondary}}></span>
                    <span className="w-3 h-4 rounded-sm" style={{background: p.accent}}></span>
                  </div>
                  <span className="truncate font-medium text-gray-700">{p.name}</span>
                </button>
              ))}
            </div>
            <p className="text-[10px] text-gray-400 mt-1.5">{tt('Выбор палитры автоматически задаёт 3 согласованных цвета. Можно также настроить цвета вручную ниже — тогда палитра станет «custom».')}</p>
          </div>

          {/* Secondary + Accent colors */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="text-xs text-gray-500 font-medium block mb-1">{tt('Вторичный цвет (secondary)')}</label>
              <div className="flex gap-2">
                <input type="color" className="w-10 h-9 border rounded cursor-pointer" value={brandForm.secondary_color} onChange={e => setBrandForm({...brandForm, secondary_color: e.target.value, theme_preset: 'custom'})} />
                <input className="flex-1 border rounded-lg px-3 py-2 text-sm font-mono" value={brandForm.secondary_color} onChange={e => setBrandForm({...brandForm, secondary_color: e.target.value, theme_preset: 'custom'})} data-testid="brand-secondary-color" />
              </div>
            </div>
            <div>
              <label className="text-xs text-gray-500 font-medium block mb-1">{tt('Акцентный цвет (accent)')}</label>
              <div className="flex gap-2">
                <input type="color" className="w-10 h-9 border rounded cursor-pointer" value={brandForm.accent_color} onChange={e => setBrandForm({...brandForm, accent_color: e.target.value, theme_preset: 'custom'})} />
                <input className="flex-1 border rounded-lg px-3 py-2 text-sm font-mono" value={brandForm.accent_color} onChange={e => setBrandForm({...brandForm, accent_color: e.target.value, theme_preset: 'custom'})} data-testid="brand-accent-color" />
              </div>
            </div>
          </div>

          {/* Live palette preview */}
          <div className="mb-4 p-3 rounded-lg border" data-testid="palette-preview" style={{background: `linear-gradient(135deg, ${brandForm.primary_color}0d, ${brandForm.accent_color}0d)`}}>
            <div className="text-[11px] text-gray-500 font-medium mb-1.5">{tt('Предпросмотр палитры')}</div>
            <div className="flex flex-wrap gap-2 items-center">
              <span className="text-white text-xs px-3 py-1.5 rounded-md font-medium" style={{background: brandForm.primary_color}}>Primary</span>
              <span className="text-white text-xs px-3 py-1.5 rounded-md font-medium" style={{background: brandForm.secondary_color}}>Secondary</span>
              <span className="text-white text-xs px-3 py-1.5 rounded-md font-medium" style={{background: brandForm.accent_color}}>Accent</span>
              <span className="text-xs text-gray-500 ml-2">{brandForm.theme_preset === 'custom' ? tt('Своя палитра') : (THEME_PRESETS.find(p => p.key === brandForm.theme_preset)?.name || brandForm.theme_preset)}</span>
            </div>
          </div>

          <button onClick={saveBrandSettings} disabled={brandSaving} className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50" data-testid="save-brand-btn">
            {brandSaving ? t('brand_saving') : t('brand_save')}
          </button>

          <hr className="my-5" />
          <h3 className="text-base font-semibold mb-1 flex items-center gap-2">{t('mp_crm_title')} {pwStatus.crm ? <span className="text-xs font-normal bg-green-100 text-green-700 px-2 py-0.5 rounded-full">{t('mp_set')}</span> : <span className="text-xs font-normal bg-red-100 text-red-600 px-2 py-0.5 rounded-full">{t('mp_not_set')}</span>}</h3>
          <p className="text-xs text-gray-400 mb-3">{t('mp_crm_desc')}</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="text-xs text-gray-500 font-medium block mb-1">{t('mp_superadmin_pw')}</label>
              <input type="password" className="w-full border rounded-lg px-3 py-2 text-sm" value={masterPwForm.current_password} onChange={e => setMasterPwForm({...masterPwForm, current_password: e.target.value})} placeholder={t('mp_your_password')} />
            </div>
            <div>
              <label className="text-xs text-gray-500 font-medium block mb-1">{t('mp_new_password')}</label>
              <input type="password" className="w-full border rounded-lg px-3 py-2 text-sm" value={masterPwForm.new_password} onChange={e => setMasterPwForm({...masterPwForm, new_password: e.target.value})} placeholder={t('mp_new_password')} data-testid="new-master-pw" />
            </div>
            <div>
              <label className="text-xs text-gray-500 font-medium block mb-1">{t('mp_confirm')}</label>
              <input type="password" className="w-full border rounded-lg px-3 py-2 text-sm" value={masterPwForm.confirm_password} onChange={e => setMasterPwForm({...masterPwForm, confirm_password: e.target.value})} placeholder={t('mp_repeat')} />
            </div>
          </div>
          <button onClick={saveMasterPassword} className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-red-700" data-testid="save-master-pw-btn">
            {t('mp_update_crm')}
          </button>

          <hr className="my-5" />
          <h3 className="text-base font-semibold mb-1 flex items-center gap-2">{t('mp_trading_title')} {pwStatus.trading ? <span className="text-xs font-normal bg-green-100 text-green-700 px-2 py-0.5 rounded-full">{t('mp_set')}</span> : <span className="text-xs font-normal bg-red-100 text-red-600 px-2 py-0.5 rounded-full">{t('mp_not_set')}</span>}</h3>
          <p className="text-xs text-gray-400 mb-3">{t('mp_trading_desc')}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="text-xs text-gray-500 font-medium block mb-1">{t('mp_superadmin_confirm')}</label>
              <input type="password" className="w-full border rounded-lg px-3 py-2 text-sm" value={tradingMasterPw.current_password} onChange={e => setTradingMasterPw({...tradingMasterPw, current_password: e.target.value})} placeholder={t('mp_your_password')} />
            </div>
            <div>
              <label className="text-xs text-gray-500 font-medium block mb-1">{t('mp_platform_pw')}</label>
              <input type="password" className="w-full border rounded-lg px-3 py-2 text-sm" value={tradingMasterPw.new_password} onChange={e => setTradingMasterPw({...tradingMasterPw, new_password: e.target.value})} placeholder={t('mp_new_password')} data-testid="new-trading-master-pw" />
            </div>
          </div>
          <button onClick={saveTradingMasterPw} className="bg-orange-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-orange-700" data-testid="save-trading-master-pw-btn">
            {t('mp_update_trading')}
          </button>

          {/* ============ PARTNERS MANAGEMENT ============ */}
          <hr className="my-5" />
          <h3 className="text-base font-semibold mb-1">{tt('Партнёры (нижняя часть сайта)')}</h3>
          <p className="text-xs text-gray-400 mb-3">
            {tt('Логотипы отображаются в футере лендинга. Рекомендуемый размер: 240×120px (соотношение 2:1), формат PNG с прозрачным фоном, максимум 500KB. Допускается SVG, JPG, WEBP до 2MB.')}
          </p>

          {/* Existing partners list */}
          {partners.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-4" data-testid="partners-list">
              {partners.map((p) => (
                <div key={p.id} className={`border rounded-lg p-3 relative ${p.enabled === false ? 'opacity-50 bg-gray-50' : 'bg-white'}`} data-testid={`partner-item-${p.id}`}>
                  <div className="flex items-center justify-center h-14 mb-2 bg-gray-50 rounded">
                    {p.logo_url ? (
                      <img src={p.logo_url} alt={p.name} className="max-h-12 max-w-full object-contain" />
                    ) : (
                      <span className="text-xs text-gray-400">{tt('нет логотипа')}</span>
                    )}
                  </div>
                  <div className="text-xs font-medium text-gray-700 truncate mb-2" title={p.name}>{p.name}</div>
                  <div className="flex items-center justify-between gap-2">
                    <button
                      onClick={() => handleTogglePartner(p)}
                      className={`text-[10px] px-2 py-1 rounded ${p.enabled === false ? 'bg-gray-200 text-gray-600' : 'bg-green-100 text-green-700'}`}
                      title={p.enabled === false ? tt('Показать') : tt('Скрыть')}
                    >
                      {p.enabled === false ? tt('Скрыт') : tt('Виден')}
                    </button>
                    <button
                      onClick={() => handleDeletePartner(p.id)}
                      className="text-[10px] text-red-600 hover:text-red-800"
                      data-testid={`delete-partner-${p.id}`}
                    >
                      {tt('Удалить')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Add new partner form */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end p-3 bg-gray-50 rounded-lg mb-2">
            <div>
              <label className="text-xs text-gray-500 font-medium block mb-1">{tt('Название партнёра')}</label>
              <input
                type="text"
                className="w-full border rounded-lg px-3 py-2 text-sm"
                value={newPartnerName}
                onChange={(e) => setNewPartnerName(e.target.value)}
                placeholder={tt('Например: Visa, Kaspi Bank')}
                data-testid="partner-name-input"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 font-medium block mb-1">{tt('Логотип (PNG/SVG/JPG, до 2MB)')}</label>
              <input
                type="file"
                accept="image/png,image/jpeg,image/svg+xml,image/webp"
                onChange={(e) => setNewPartnerFile(e.target.files?.[0] || null)}
                className="w-full text-xs"
                data-testid="partner-logo-input"
              />
              {newPartnerFile && (
                <p className="text-[10px] text-gray-500 mt-0.5">{newPartnerFile.name} ({(newPartnerFile.size/1024).toFixed(0)} KB)</p>
              )}
            </div>
            <button
              onClick={handleAddPartner}
              disabled={partnerSaving || !newPartnerName.trim() || !newPartnerFile}
              className="bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-emerald-700 disabled:opacity-50"
              data-testid="add-partner-btn"
            >
              {partnerSaving ? tt('Загрузка...') : tt('Добавить партнёра')}
            </button>
          </div>
          <p className="text-[10px] text-gray-400">
            {tt('💡 Совет: используйте PNG с прозрачным фоном на белой карточке футера — логотипы выглядят гармонично. Изображения автоматически масштабируются до высоты 56px.')}
          </p>

          {/* ============ FACTORY RESET (WHITE-LABEL WIPE) ============ */}
          <hr className="my-5 border-red-200" />
          <div className="p-4 border-2 border-red-200 rounded-lg bg-red-50" data-testid="factory-reset-panel">
            <h3 className="text-base font-semibold text-red-900 mb-1 flex items-center gap-2">
              <span className="text-red-600">⚠️</span>
              {tt('Заводской сброс (White-Label)')}
            </h3>
            <p className="text-xs text-red-700 mb-3">
              {tt('Полностью очистить базу для нового бренда — без следов прошлых данных. Используйте при развёртывании на новом домене.')}
            </p>

            <div className="p-3 mb-3 bg-white border border-red-200 rounded-lg text-xs text-red-800">
              <b>{tt('Будет удалено безвозвратно:')}</b>
              <ul className="list-disc ml-5 mt-1 space-y-0.5">
                <li>{tt('Все клиенты, депозиты, выводы, платёжные инструменты')}</li>
                <li>{tt('Все CFD/Options сделки, торговые аккаунты, история')}</li>
                <li>{tt('Все сотрудники (кроме текущего суперадмина)')}</li>
                <li>{tt('Комментарии, документы, KYC, посещаемость, зарплаты')}</li>
                <li>{tt('Настройки бренда, партнёры, IP whitelist, шаблоны')}</li>
              </ul>
              <div className="mt-2 pt-2 border-t border-red-200">
                <b>{tt('Сохраняется:')}</b> {tt('только ваш текущий суперадмин-аккаунт для входа.')}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
              <div>
                <label className="text-xs text-gray-700 font-medium block mb-1">{tt('Мастер-пароль')}</label>
                <input
                  type="password"
                  value={factoryResetPw}
                  onChange={(e) => setFactoryResetPw(e.target.value)}
                  placeholder={tt('Введите мастер-пароль')}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  data-testid="factory-reset-pw-input"
                  autoComplete="new-password"
                />
              </div>
              <div>
                <label className="text-xs text-gray-700 font-medium block mb-1">
                  {tt('Введите для подтверждения:')} <code className="bg-white px-1.5 py-0.5 rounded text-red-700 font-mono">УДАЛИТЬ ВСЁ</code>
                </label>
                <input
                  type="text"
                  value={factoryResetConfirm}
                  onChange={(e) => setFactoryResetConfirm(e.target.value)}
                  placeholder="УДАЛИТЬ ВСЁ"
                  className="w-full border rounded-lg px-3 py-2 text-sm font-mono"
                  data-testid="factory-reset-confirm-input"
                />
              </div>
            </div>

            <button
              onClick={handleFactoryReset}
              disabled={factoryResetting || factoryResetConfirm !== 'УДАЛИТЬ ВСЁ' || !factoryResetPw}
              className="w-full py-2.5 px-4 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center font-medium text-sm"
              data-testid="factory-reset-btn"
            >
              {factoryResetting ? tt('Выполняется полная очистка...') : tt('Выполнить заводской сброс')}
            </button>

            {factoryResetResult && (
              <div className={`mt-3 p-2.5 rounded-lg text-xs ${factoryResetResult.status === 'success' ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-100 border border-red-300 text-red-800'}`} data-testid="factory-reset-result">
                <div className="font-medium mb-1">{factoryResetResult.message || factoryResetResult.status}</div>
                {factoryResetResult.status === 'success' && (
                  <div className="text-gray-700 space-y-0.5">
                    <div>{tt('Удалено коллекций:')} <b>{factoryResetResult.dropped?.length || 0}</b></div>
                    <div>{tt('Сохранён пользователь:')} <b>{factoryResetResult.preserved_user}</b></div>
                    <div className="mt-1 text-amber-700">{tt('Страница будет перезагружена автоматически...')}</div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center p-12">
          <div className="spinner mr-3"></div>
          <span className="text-gray-600">{t('loading')}</span>
        </div>
      ) : (
        <>
          {/* Month Filter + Status Button */}
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3" data-testid="dashboard-filters">
            <div className="flex items-center gap-2">
              {/* Year navigation */}
              <div className="flex items-center bg-white border rounded-lg shadow-sm">
                <button
                  onClick={() => setFilterYear(y => y - 1)}
                  className="px-2 py-2 hover:bg-gray-50 rounded-l-lg border-r text-gray-500 hover:text-gray-700"
                  data-testid="year-prev"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="px-3 py-2 text-sm font-semibold text-gray-700 min-w-[60px] text-center" data-testid="year-display">{filterYear}</span>
                <button
                  onClick={() => setFilterYear(y => y + 1)}
                  className="px-2 py-2 hover:bg-gray-50 rounded-r-lg border-l text-gray-500 hover:text-gray-700"
                  data-testid="year-next"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>

              {/* Month pills */}
              <div className="flex items-center gap-1 flex-wrap">
                <button
                  onClick={() => setFilterMonth('')}
                  className={`px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors ${
                    !filterMonth
                      ? 'bg-gray-800 text-white shadow-sm'
                      : 'bg-white border text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                  }`}
                  data-testid="month-btn-all"
                >
                  {tt('Все')}
                </button>
                {Array.from({ length: 12 }, (_, i) => {
                  const m = i + 1;
                  const label = new Date(filterYear, i).toLocaleString('ru', { month: 'short' }).replace('.', '');
                  const isActive = filterMonth === m;
                  return (
                    <button
                      key={m}
                      onClick={() => setFilterMonth(m)}
                      className={`px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors ${
                        isActive
                          ? 'bg-indigo-600 text-white shadow-sm'
                          : 'bg-white border text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                      }`}
                      data-testid={`month-btn-${m}`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>

            {user?.role === 'superadmin' && (
              <button
                onClick={() => navigate('/crm/stats')}
                className="flex items-center gap-2 px-3 py-1.5 bg-white border border-emerald-200 text-emerald-700 rounded-lg hover:bg-emerald-50 transition-colors shadow-sm text-xs font-medium"
                data-testid="goto-stats-btn-filter"
              >
                <BarChart3 className="w-3.5 h-3.5" />
                {t('dash_stats_by_department')}
              </button>
            )}
          </div>

          {/* Main Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="card p-6 relative">
              <button type="button" title={tt('Клиенты, зарегистрированные в выбранном периоде (или назначенные сейл-менеджерам, для не-superadmin)')} className="absolute top-3 right-3 w-5 h-5 rounded-full bg-blue-50 hover:bg-blue-100 text-blue-600 flex items-center justify-center transition-colors" data-testid="dash-help-clients">
                <HelpCircle className="w-3.5 h-3.5" />
              </button>
              <div className="flex items-center">
                <div className="p-3 bg-blue-100 rounded-full">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">{t('dash_total_clients')}</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.totalClients}</p>
                </div>
              </div>
            </div>

            <div className="card p-6 relative">
              <button type="button" title={tt('Уникальные клиенты, у которых первый депозит (FTD) был создан в выбранном периоде. Чистый = текущий баланс ≥ 95% от FTD-суммы; иначе грязный.')} className="absolute top-3 right-3 w-5 h-5 rounded-full bg-blue-50 hover:bg-blue-100 text-blue-600 flex items-center justify-center transition-colors" data-testid="dash-help-ftd">
                <HelpCircle className="w-3.5 h-3.5" />
              </button>
              <div className="flex items-center">
                <div className="p-3 bg-green-100 rounded-full">
                  <TrendingUp className="w-6 h-6 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">{t('dash_ftd_clients')}</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.ftdClients}</p>
                  {(stats.ftdClean > 0 || stats.ftdDirty > 0) && (
                    <p className="text-xs text-gray-400">
                      <span className="text-green-600">чист: {stats.ftdClean}</span>
                      {' / '}
                      <span className="text-orange-500">гряз: {stats.ftdDirty}</span>
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="card p-6 relative">
              <button type="button" title={tt('Сумма всех депозитов всех типов (FTD + Reload + Deposit), созданных в выбранном периоде')} className="absolute top-3 right-3 w-5 h-5 rounded-full bg-blue-50 hover:bg-blue-100 text-blue-600 flex items-center justify-center transition-colors" data-testid="dash-help-deposits">
                <HelpCircle className="w-3.5 h-3.5" />
              </button>
              <div className="flex items-center">
                <div className="p-3 bg-yellow-100 rounded-full">
                  <Target className="w-6 h-6 text-yellow-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">{t('dash_deposits_sum')}</p>
                  <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats.totalDeposits, true)}</p>
                  <p className="text-xs text-gray-400">{stats.depositsCount} шт.</p>
                </div>
              </div>
            </div>

            <div className="card p-6 relative">
              <button type="button" title={tt('Конверсия = FTD / Новые лиды × 100%. Чистая считается только по чистым FTD, грязная — по грязным.')} className="absolute top-3 right-3 w-5 h-5 rounded-full bg-blue-50 hover:bg-blue-100 text-blue-600 flex items-center justify-center transition-colors" data-testid="dash-help-conversion">
                <HelpCircle className="w-3.5 h-3.5" />
              </button>
              <div className="flex items-center">
                <div className="p-3 bg-purple-100 rounded-full">
                  <BarChart3 className="w-6 h-6 text-purple-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">{t('dash_conversion')}</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.conversionRate}%</p>
                  {(stats.conversionClean > 0 || stats.conversionDirty > 0) && (
                    <p className="text-xs text-gray-400">
                      <span className="text-green-600">чист: {stats.conversionClean}%</span>
                      {' / '}
                      <span className="text-orange-500">гряз: {stats.conversionDirty}%</span>
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Collapsible Department Statistics - Sales vs Retention */}
          {['superadmin', 'team_lead_sales', 'team_lead_retention'].includes(user?.role) && (
            <div className="card p-6 mb-8">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <Target className="w-6 h-6 text-purple-600 mr-3" />
                  <h3 className="text-xl font-semibold text-gray-900">
                    {user?.role === 'team_lead_sales' ? t('dash_sales_dept') : 
                     user?.role === 'team_lead_retention' ? t('dash_retention_dept') : 
                     t('dash_department_stats')}
                  </h3>
                </div>
                
                {/* Collapse Button */}
                <button
                  onClick={() => setDepartmentStatsCollapsed(!departmentStatsCollapsed)}
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                  title={departmentStatsCollapsed ? "Развернуть статистику" : "Свернуть статистику"}
                >
                  <ChevronDown className={`w-5 h-5 transition-transform ${!departmentStatsCollapsed ? 'rotate-180' : ''}`} />
                </button>
              </div>
              
              {/* Collapsible Content */}
              {!departmentStatsCollapsed && (
                <div className={`grid grid-cols-1 ${user?.role === 'superadmin' ? 'lg:grid-cols-2' : ''} gap-6`}>
                  {/* Sales Department - visible for superadmin and team_lead_sales */}
                  {(user?.role === 'superadmin' || user?.role === 'team_lead_sales') && (
                  <div className="bg-blue-50 rounded-lg p-4">
                    <div className="flex items-center mb-4">
                      <UserPlus className="w-5 h-5 text-blue-600 mr-2" />
                      <h4 className="text-lg font-semibold text-gray-900">{t('dash_sales_dept')}</h4>
                    </div>
                    <div className="space-y-3">
                      {managerStats.sales.length === 0 ? (
                        <p className="text-gray-500 text-sm">{tt('Менеджеры не найдены')}</p>
                      ) : (
                        managerStats.sales.map((manager, index) => (
                          <div key={index} className="flex justify-between items-center p-3 bg-white rounded-lg shadow-sm">
                            <div>
                              <p className="font-medium text-gray-900">{manager.name}</p>
                              <p className="text-sm text-gray-600">
                                {manager.clients} {t('dash_clients_count')} • {manager.ftdClients} {t('dash_ftd')}
                                {(manager.ftdClean > 0 || manager.ftdDirty > 0) && (
                                  <span className="text-xs ml-1">
                                    (<span className="text-green-600">{manager.ftdClean}</span>/<span className="text-orange-500">{manager.ftdDirty}</span>)
                                  </span>
                                )}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="font-semibold text-blue-600">{formatCurrency(manager.depositAmount, true)}</p>
                              <p className="text-xs text-gray-500">{manager.deposits} {t('dash_deposits')}</p>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                  )}

                  {/* Retention Department - visible for superadmin and team_lead_retention */}
                  {(user?.role === 'superadmin' || user?.role === 'team_lead_retention') && (
                  <div className="bg-green-50 rounded-lg p-4">
                    <div className="flex items-center mb-4">
                      <UserCheck className="w-5 h-5 text-green-600 mr-2" />
                      <h4 className="text-lg font-semibold text-gray-900">{t('dash_retention_dept')}</h4>
                    </div>
                    <div className="space-y-3">
                      {managerStats.retention.length === 0 ? (
                        <p className="text-gray-500 text-sm">{tt('Менеджеры не найдены')}</p>
                      ) : (
                        managerStats.retention.map((manager, index) => (
                          <div key={index} className="flex justify-between items-center p-3 bg-white rounded-lg shadow-sm">
                            <div>
                              <p className="font-medium text-gray-900">{manager.name}</p>
                              <p className="text-sm text-gray-600">
                                {manager.clients} {t('dash_clients_count')} • {manager.ftdClients} {t('dash_ftd')}
                                {(manager.ftdClean > 0 || manager.ftdDirty > 0) && (
                                  <span className="text-xs ml-1">
                                    (<span className="text-green-600">{manager.ftdClean}</span>/<span className="text-orange-500">{manager.ftdDirty}</span>)
                                  </span>
                                )}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="font-semibold text-green-600">{formatCurrency(manager.depositAmount, true)}</p>
                              <p className="text-xs text-gray-500">{manager.deposits} {t('dash_deposits')}</p>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Deleted Managers - show only if there are deposits from deleted managers */}
          {user?.role === 'superadmin' && managerStats.deleted && managerStats.deleted.length > 0 && (
            <div className="card p-6 mb-8">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <Activity className="w-6 h-6 text-red-600 mr-3" />
                  <h3 className="text-xl font-semibold text-gray-900">{tt('Удаленные менеджеры')}</h3>
                  <span className="ml-2 text-sm text-gray-500">{tt('(депозиты сохранены в истории)')}</span>
                </div>
                <button
                  onClick={() => setDeletedManagersCollapsed(!deletedManagersCollapsed)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                  title={deletedManagersCollapsed ? "Развернуть" : "Свернуть"}
                >
                  <ChevronDown className={`w-5 h-5 transform transition-transform ${!deletedManagersCollapsed ? 'rotate-180' : ''}`} />
                </button>
              </div>
              
              {!deletedManagersCollapsed && (
                <div className="space-y-3">
                  {managerStats.deleted.map((manager, index) => (
                    <div key={index} className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                      <div>
                        <p className="font-medium text-gray-900">{manager.name}</p>
                        <p className="text-sm text-red-600">{tt('Менеджер был удален, но депозиты сохранены')}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-red-600">{formatCurrency(manager.depositAmount, true)}</p>
                        <p className="text-xs text-gray-500">{manager.deposits} депозитов</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Import Statistics Section */}
          {user?.role === 'superadmin' && importStats.length > 0 && (
            <div className="card p-6 mb-8">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <FileText className="w-6 h-6 text-emerald-600 mr-3" />
                  <h3 className="text-xl font-semibold text-gray-900">{tt('Статистика импорта (Google Sheets / Excel)')}</h3>
                </div>
                <button
                  onClick={() => setImportStatsCollapsed(!importStatsCollapsed)}
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                  title={importStatsCollapsed ? "Развернуть" : "Свернуть"}
                >
                  <ChevronDown className={`w-5 h-5 transition-transform ${!importStatsCollapsed ? 'rotate-180' : ''}`} />
                </button>
              </div>
              
              {!importStatsCollapsed && (
                <div className="space-y-6">
                  {importStats.map((imp, idx) => (
                    <div key={idx} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-lg font-medium text-gray-800">{imp.label}</h4>
                        <span className="text-sm text-gray-500">{imp.totalClients} клиентов</span>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                        <div className="bg-blue-50 rounded-lg p-3">
                          <div className="text-sm text-blue-600 font-medium">{tt('Всего клиентов')}</div>
                          <div className="text-xl font-bold text-blue-800">{imp.totalClients}</div>
                        </div>
                        <div className="bg-green-50 rounded-lg p-3">
                          <div className="text-sm text-green-600 font-medium">FTD</div>
                          <div className="text-xl font-bold text-green-800">{imp.ftdClients}</div>
                        </div>
                        <div className="bg-purple-50 rounded-lg p-3">
                          <div className="text-sm text-purple-600 font-medium">{tt('Конверсия')}</div>
                          <div className="text-xl font-bold text-purple-800">{imp.conversionRate}%</div>
                        </div>
                        <div className="bg-amber-50 rounded-lg p-3">
                          <div className="text-sm text-amber-600 font-medium">{tt('Депозитов')}</div>
                          <div className="text-xl font-bold text-amber-800">${imp.totalDeposits?.toLocaleString()}</div>
                        </div>
                      </div>
                      
                      {imp.campaigns && imp.campaigns.length > 0 && (
                        <div className="mt-3">
                          <p className="text-sm text-gray-500 mb-2">{tt('Топ кампаний:')}</p>
                          <div className="overflow-x-auto">
                            <table className="min-w-full text-sm">
                              <thead>
                                <tr className="text-left text-gray-500 border-b">
                                  <th className="pb-2 pr-4">{tt('Кампания')}</th>
                                  <th className="pb-2 pr-4">{tt('Клиенты')}</th>
                                  <th className="pb-2 pr-4">FTD</th>
                                  <th className="pb-2">{tt('Конверсия')}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {imp.campaigns.slice(0, 10).map((camp, campIdx) => (
                                  <tr key={campIdx} className="border-b border-gray-100">
                                    <td className="py-2 pr-4 font-medium">{camp.name}</td>
                                    <td className="py-2 pr-4">{camp.totalClients}</td>
                                    <td className="py-2 pr-4">{camp.ftdClients}</td>
                                    <td className="py-2">{camp.conversionRate}%</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Collapsible Affiliates Section */}
          {user?.role === 'superadmin' && (
            <div className="card p-6 mb-8">
              <div 
                className="flex items-center justify-between mb-4 cursor-pointer select-none"
                onClick={() => setAffiliatesCollapsed(!affiliatesCollapsed)}
                data-testid="affiliates-toggle"
              >
                <div className="flex items-center">
                  <Users className="w-6 h-6 text-indigo-600 mr-3" />
                  <h3 className="text-xl font-semibold text-gray-900">{tt('Аффилейты')}</h3>
                </div>
                
                <div
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                  title={affiliatesCollapsed ? "Развернуть аффилейтов" : "Свернуть аффилейтов"}
                >
                  <ChevronDown className={`w-5 h-5 transition-transform ${!affiliatesCollapsed ? 'rotate-180' : ''}`} />
                </div>
              </div>
              
              {/* Collapsible Content */}
              {!affiliatesCollapsed && (
                <div className="space-y-4">
                  {affiliateStats.length === 0 ? (
                    <p className="text-gray-500 text-sm">{tt('Аффилейты не найдены')}</p>
                  ) : (
                    <div className="space-y-6">
                      {affiliateStats.map((affiliate, index) => (
                        <div key={index} className="border border-indigo-200 rounded-lg overflow-hidden">
                          {/* Affiliate Summary */}
                          <div className="bg-indigo-50 p-4">
                            <div className="flex justify-between items-start mb-2">
                              <h4 className="font-medium text-gray-900 truncate">{affiliate.name}</h4>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => handleEditCampaigns(affiliate)}
                                  className="px-2 py-1 text-xs bg-indigo-100 text-indigo-700 rounded hover:bg-indigo-200 transition-colors"
                                  data-testid={`link-campaigns-${affiliate.name}`}
                                >
                                  {tt('Кампании')}
                                </button>
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  affiliate.status === 'active' ? 'bg-green-100 text-green-700' : 
                                  affiliate.status === 'deleted' ? 'bg-red-100 text-red-700' : 
                                  'bg-gray-100 text-gray-600'
                                }`}>
                                  {affiliate.status === 'active' ? 'Активен' : 
                                   affiliate.status === 'deleted' ? 'Удален' : 
                                   'Неактивен'}
                                </span>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                              <div className="text-sm">
                                <span className="text-gray-600 block">{tt('Клиенты:')}</span>
                                <span className="font-medium text-indigo-600">{affiliate.totalClients}</span>
                              </div>
                              <div className="text-sm">
                                <span className="text-gray-600 block">FTD:</span>
                                <span className="font-medium text-green-600">{affiliate.ftdClients}</span>
                                {(affiliate.ftdClean > 0 || affiliate.ftdDirty > 0) && (
                                  <span className="text-xs ml-1 text-gray-400">
                                    (<span className="text-green-600">{affiliate.ftdClean}</span>/<span className="text-orange-500">{affiliate.ftdDirty}</span>)
                                  </span>
                                )}
                              </div>
                              <div className="text-sm">
                                <span className="text-gray-600 block">{tt('Конверсия:')}</span>
                                <span className="font-medium text-yellow-600">{affiliate.conversionRate}%</span>
                                {(affiliate.conversionClean > 0 || affiliate.conversionDirty > 0) && (
                                  <div className="text-xs text-gray-400">
                                    <span className="text-green-600">чист: {affiliate.conversionClean}%</span>
                                    {' / '}
                                    <span className="text-orange-500">гряз: {affiliate.conversionDirty}%</span>
                                  </div>
                                )}
                              </div>
                              <div className="text-sm">
                                <span className="text-gray-600 block">{tt('Баланс:')}</span>
                                <span className="font-medium text-purple-600">{formatCurrency(affiliate.totalBalance, true)}</span>
                              </div>
                            </div>
                          </div>
                          
                          {/* Campaign Breakdown */}
                          {affiliate.campaigns && affiliate.campaigns.length > 0 && (
                            <div className="bg-white p-4">
                              <h5 className="text-sm font-medium text-gray-700 mb-3">{tt('Источники трафика:')}</h5>
                              <div className="space-y-2">
                                {affiliate.campaigns.map((campaign, campIndex) => {
                                  const campKey = `${affiliate.name}_${campaign.name}`;
                                  const isStatusOpen = expandedCampaignStatuses[campKey];
                                  const statusLabels = {
                                    new: 'Новый', ftd: 'FTD', in_progress: 'В работе',
                                    potential: 'Потенциал', not_potential: 'Не потенциал',
                                    not_interested: 'Не интересно', no_answer: 'Нет ответа',
                                    replace: 'Замена', fake: 'Фейк', duplicate: 'Дубль',
                                    no_russian: 'Без рус.', under_18: 'До 18',
                                  };
                                  const statusColors = {
                                    new: 'bg-blue-100 text-blue-700', ftd: 'bg-green-100 text-green-700',
                                    in_progress: 'bg-amber-100 text-amber-700', potential: 'bg-emerald-100 text-emerald-700',
                                    not_potential: 'bg-gray-100 text-gray-600', not_interested: 'bg-red-100 text-red-600',
                                    no_answer: 'bg-orange-100 text-orange-600', replace: 'bg-purple-100 text-purple-600',
                                    fake: 'bg-rose-100 text-rose-600', duplicate: 'bg-slate-100 text-slate-600',
                                    no_russian: 'bg-cyan-100 text-cyan-600', under_18: 'bg-pink-100 text-pink-600',
                                  };
                                  return (
                                    <div key={campIndex} className="py-2 px-3 bg-gray-50 rounded-lg">
                                      <div className="flex items-center justify-between">
                                        <div className="flex-1 min-w-0">
                                          <p className="text-sm font-medium text-gray-900 truncate">{campaign.name}</p>
                                          <div className="flex items-center gap-4 mt-1">
                                            <span className="text-xs text-gray-500">
                                              {tt('Лидов:')} <span className="font-semibold text-indigo-600">{campaign.totalClients}</span>
                                            </span>
                                            <span className="text-xs text-gray-500">
                                              FTD: <span className="font-semibold text-green-600">{campaign.ftdClients}</span>
                                            </span>
                                            <span className="text-xs text-gray-500">
                                              {tt('Конв:')} <span className="font-semibold text-yellow-600">{campaign.conversionRate}%</span>
                                            </span>
                                            <span className="text-xs text-gray-500">
                                              {tt('Сумма:')} <span className="font-semibold text-purple-600">{formatCurrency(campaign.totalBalance, true)}</span>
                                            </span>
                                          </div>
                                        </div>
                                        <button
                                          onClick={() => setExpandedCampaignStatuses(prev => ({...prev, [campKey]: !prev[campKey]}))}
                                          className={`ml-2 px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                                            isStatusOpen
                                              ? 'bg-indigo-600 text-white'
                                              : 'bg-white border border-indigo-200 text-indigo-600 hover:bg-indigo-50'
                                          }`}
                                          data-testid={`campaign-statuses-${campIndex}`}
                                        >
                                          <ListFilter className="w-3 h-3 inline mr-1" />
                                          {tt('Статусы')}
                                        </button>
                                      </div>
                                      {isStatusOpen && campaign.statuses && (
                                        <div className="mt-2 pt-2 border-t border-gray-200 flex flex-wrap gap-1.5">
                                          {Object.entries(campaign.statuses)
                                            .sort((a, b) => b[1] - a[1])
                                            .map(([status, count]) => (
                                              <span key={status} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${statusColors[status] || 'bg-gray-100 text-gray-600'}`}>
                                                {statusLabels[status] || status} <span className="font-bold">{count}</span>
                                              </span>
                                            ))}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Campaign Linking Modal */}
          {editingAffiliate && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setEditingAffiliate(null)}>
              <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
                <div className="p-5 border-b">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Кампании аффилиата "{editingAffiliate.name}"
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">{tt('Выберите кампании, которые принадлежат этому аффилиату')}</p>
                </div>
                
                <div className="p-4 border-b">
                  <input
                    type="text"
                    placeholder={tt('Поиск кампании...')}
                    value={campaignSearch}
                    onChange={(e) => setCampaignSearch(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
                    data-testid="campaign-search-input"
                  />
                  {selectedCampaigns.length > 0 && (
                    <div className="mt-2 text-sm text-indigo-600">
                      Выбрано: {selectedCampaigns.length} кампаний
                    </div>
                  )}
                </div>
                
                <div className="overflow-y-auto flex-1 p-4">
                  {allCampaigns
                    .filter(c => !campaignSearch || c.name.toLowerCase().includes(campaignSearch.toLowerCase()))
                    .map((campaign, idx) => (
                      <label
                        key={idx}
                        className={`flex items-center justify-between p-2 rounded cursor-pointer hover:bg-gray-50 ${
                          selectedCampaigns.includes(campaign.name) ? 'bg-indigo-50' : ''
                        }`}
                      >
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            checked={selectedCampaigns.includes(campaign.name)}
                            onChange={() => toggleCampaign(campaign.name)}
                            className="mr-3 rounded text-indigo-600"
                          />
                          <span className="text-sm">{campaign.name}</span>
                        </div>
                        <span className="text-xs text-gray-400">{campaign.count} кл.</span>
                      </label>
                    ))
                  }
                </div>
                
                <div className="p-4 border-t flex justify-end gap-3">
                  <button
                    onClick={() => setEditingAffiliate(null)}
                    className="px-4 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50"
                  >
                    {tt('Отмена')}
                  </button>
                  <button
                    onClick={handleSaveCampaigns}
                    className="px-4 py-2 text-sm text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
                    data-testid="save-campaigns-btn"
                  >
                    {tt('Сохранить')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Monthly Statistics */}
          <div className="card p-6 mb-8">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <Calendar className="w-6 h-6 text-gray-600 mr-3" />
                <h3 className="text-xl font-semibold text-gray-900">{tt('Статистика по месяцам')}</h3>
              </div>
              <button
                onClick={() => setMonthlyStatsCollapsed(!monthlyStatsCollapsed)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                title={monthlyStatsCollapsed ? "Развернуть" : "Свернуть"}
              >
                <ChevronDown className={`w-5 h-5 transform transition-transform ${!monthlyStatsCollapsed ? 'rotate-180' : ''}`} />
              </button>
            </div>
            
            {!monthlyStatsCollapsed && (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {monthlyStats.map((month, index) => (
                  <div key={index} className="bg-gray-50 p-4 rounded-lg text-center">
                    <p className="text-sm font-medium text-gray-600 mb-2">{month.month}</p>
                    <p className="text-lg font-bold text-blue-600">{month.clients}</p>
                    <p className="text-xs text-gray-500 mb-1">{tt('клиентов')}</p>
                    <p className="text-lg font-bold text-green-600">{formatCurrency(month.depositAmount, true)}</p>
                    <p className="text-xs text-gray-500">{tt('депозитов')}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

        </>
      )}

      {/* Backup password modal */}
      {showBackupPassword && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex items-center gap-2 mb-4">
              <Database className="w-5 h-5 text-blue-600" />
              <h3 className="text-lg font-semibold">{tt('Скачать бэкап БД')}</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">{tt('Введите мастер-пароль для скачивания полного бэкапа базы данных')}</p>
            <form onSubmit={async (e) => {
              e.preventDefault();
              const pw = e.target.elements.backup_pw.value;
              if (!pw) return;
              setBackupLoading(true);
              setShowBackupPassword(false);
              try {
                const token = localStorage.getItem('token');
                const res = await fetch(`${API}/database-backup`, {
                  method: 'POST',
                  headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                  body: JSON.stringify({ master_password: pw })
                });
                if (!res.ok) {
                  const err = await res.json().catch(() => ({}));
                  throw new Error(err.detail || 'Ошибка');
                }
                // [BACKUP-2026-05-14] Stream-pipe to disk via ReadableStream + Blob URL.
                // The previous `res.blob()` buffered the entire backup in browser memory
                // (could crash the tab on 100+ MB exports). Stream the body through a
                // pump so the browser flushes chunks straight into the download blob.
                const reader = res.body?.getReader();
                const chunks = [];
                let totalBytes = 0;
                if (reader) {
                  while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;
                    chunks.push(value);
                    totalBytes += value.length;
                  }
                } else {
                  chunks.push(await res.arrayBuffer());
                }
                const blob = new Blob(chunks, { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `crm_full_backup_${new Date().toISOString().slice(0,10)}.json`;
                a.click();
                URL.revokeObjectURL(url);
                console.log(`Backup downloaded: ${(totalBytes/1024/1024).toFixed(2)} MB`);
              } catch (err) {
                alert(err.message);
              } finally {
                setBackupLoading(false);
              }
            }}>
              <input name="backup_pw" type="password" autoFocus className="w-full border rounded-lg px-3 py-2 mb-3" placeholder={tt('Мастер-пароль')} data-testid="backup-password-input" />
              <div className="flex gap-2">
                <button type="button" onClick={() => setShowBackupPassword(false)} className="flex-1 border rounded-lg py-2 text-sm hover:bg-gray-50">{tt('Отмена')}</button>
                <button type="submit" className="flex-1 bg-blue-600 text-white rounded-lg py-2 text-sm hover:bg-blue-700" data-testid="backup-confirm-btn">{tt('Скачать')}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;